import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import AuthService from "../services/auth.service";

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  adminOnly = false,
}) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  const location = useLocation();

  // Validate token on component mount
  useEffect(() => {
    const validateAuth = async () => {
      try {
        if (token && userStr) {
          // Check token validity with backend
          const isValid = await AuthService.validateToken();
          setIsAuthenticated(isValid);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Token validation failed:", error);
        setIsAuthenticated(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateAuth();
  }, [token, userStr]);

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Not authenticated at all
  if (!isAuthenticated) {
    // Store the current path to redirect back after login
    sessionStorage.setItem("redirectAfterLogin", location.pathname);
    return <Navigate to="/login" replace />;
  } // Check for admin route protection
  if (adminOnly && userStr) {
    try {
      const user = JSON.parse(userStr);
      if (![2, 3, 5].includes(user.role_id)) {
        return <Navigate to="/dashboard" replace />;
      }
    } catch (error) {
      // Invalid JSON in localStorage
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      return <Navigate to="/login" replace />;
    }
  }

  // Check if user is trying to access admin dashboard
  if (location.pathname === "/admin" && userStr) {
    try {
      const user = JSON.parse(userStr);
      if (![2, 3, 5].includes(user.role_id)) {
        return <Navigate to="/dashboard" replace />;
      }
    } catch (error) {
      // Invalid JSON in localStorage
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
